import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Plus, Pencil, Trash2, Gift, X, Image } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAuthHeaders() {
  const token = localStorage.getItem("token");
  return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
}

export default function CorporateGiftingItemsPage() {
  const [items, setItems] = useState([]);
  const [colors, setColors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", images: [""], starting_price: 0, available_colors: [], category_id: "" });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/gifting-items`, { headers: getAuthHeaders(), withCredentials: true }),
      axios.get(`${API}/colors`, { headers: getAuthHeaders(), withCredentials: true }),
      axios.get(`${API}/gifting-categories`, { headers: getAuthHeaders(), withCredentials: true }),
    ]).then(([itemsRes, colorsRes, catsRes]) => {
      setItems(itemsRes.data);
      setColors(colorsRes.data);
      setCategories(catsRes.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const fetchItems = async () => {
    const { data } = await axios.get(`${API}/gifting-items`, { headers: getAuthHeaders(), withCredentials: true });
    setItems(data);
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ name: "", description: "", images: [""], starting_price: 0, available_colors: [], category_id: "" });
    setFormError("");
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name,
      description: item.description || "",
      images: item.images?.length ? [...item.images] : [""],
      starting_price: item.starting_price || 0,
      available_colors: item.available_colors || [],
      category_id: item.category_id || "",
    });
    setFormError("");
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError("");
    const payload = { ...form, images: form.images.filter(img => img.trim()) };
    try {
      if (editing) {
        await axios.put(`${API}/gifting-items/${editing.id}`, payload, { headers: getAuthHeaders(), withCredentials: true });
      } else {
        await axios.post(`${API}/gifting-items`, payload, { headers: getAuthHeaders(), withCredentials: true });
      }
      setShowForm(false);
      fetchItems();
    } catch (err) {
      setFormError(err.response?.data?.detail || "Something went wrong.");
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await axios.delete(`${API}/gifting-items/${id}`, { headers: getAuthHeaders(), withCredentials: true });
      fetchItems();
    } catch (err) { console.error(err); }
  };

  const toggleColor = (colorId) => {
    setForm(prev => ({
      ...prev,
      available_colors: prev.available_colors.includes(colorId)
        ? prev.available_colors.filter(c => c !== colorId)
        : [...prev.available_colors, colorId]
    }));
  };

  const addImageField = () => setForm(prev => ({ ...prev, images: [...prev.images, ""] }));
  const updateImage = (idx, val) => { const imgs = [...form.images]; imgs[idx] = val; setForm({...form, images: imgs}); };
  const removeImage = (idx) => { if (form.images.length > 1) setForm({...form, images: form.images.filter((_, i) => i !== idx)}); };

  const getCategoryName = (id) => categories.find(c => c.id === id)?.name || "—";
  const getColorInfo = (id) => colors.find(c => c.id === id);

  return (
    <div data-testid="page-gifting-items" className="min-h-screen bg-[#F4F3EF]">
      <div className="bg-white border-b border-[#161616] px-5 sm:px-8 py-6 gap-4 flex-wrap flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-[-0.02em] text-[#161616]">Corporate Gifting Items</h1>
        <button onClick={openAdd} data-testid="add-item-btn"
          className="inline-flex items-center gap-2 bg-[#161616] text-white font-bold px-5 py-2.5 text-sm hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_#E5007D] transition-all duration-200">
          <Plus size={16} /> Add Item
        </button>
      </div>

      <div className="px-5 sm:px-8 py-6">
        {loading ? (
          <div className="text-center py-12 text-[#5A5955]">Loading...</div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-[#D9D7D0] p-12 text-center">
            <Gift size={36} className="mx-auto text-[#D9D7D0] mb-4" />
            <p className="text-sm text-[#5A5955]">No items yet. Add one to get started.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {items.map((item) => (
              <div key={item.id} data-testid={`item-card-${item.id}`} className="bg-white border border-[#D9D7D0] overflow-hidden group hover:shadow-lg transition-shadow">
                {item.images?.[0] ? (
                  <img src={item.images[0]} alt={item.name} className="w-full h-44 object-cover" />
                ) : (
                  <div className="w-full h-44 bg-[#F4F3EF] flex items-center justify-center">
                    <Image size={32} className="text-[#D9D7D0]" />
                  </div>
                )}
                <div className="p-5 space-y-3">
                  <div>
                    <h3 className="text-sm font-bold text-[#161616] mb-1">{item.name}</h3>
                    {item.category_id && <p className="text-xs font-bold text-[#E5007D]">{getCategoryName(item.category_id)}</p>}
                  </div>
                  <p className="text-xs text-[#5A5955] line-clamp-2">{item.description || "No description"}</p>
                  <p className="text-base font-black text-[#161616]">Starting &#8377;{item.starting_price}</p>
                  {item.available_colors?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {item.available_colors.map(cid => {
                        const ci = getColorInfo(cid);
                        return ci ? <div key={cid} className="w-5 h-5 border border-[#D9D7D0]" style={{ backgroundColor: ci.color_code }} title={ci.name} /> : null;
                      })}
                    </div>
                  )}
                  <div className="flex items-center gap-2 pt-2 border-t border-[#D9D7D0]">
                    <button onClick={() => openEdit(item)} className="p-2 hover:bg-[#F4F3EF] transition-colors"><Pencil size={14} className="text-[#5A5955]" /></button>
                    <button onClick={() => handleDelete(item.id)} className="p-2 hover:bg-red-50 transition-colors"><Trash2 size={14} className="text-red-500" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showForm && createPortal(
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="item-modal">
            <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-[#161616]">
              <h2 className="text-xl font-bold tracking-tight text-[#161616]">{editing ? "Edit Item" : "Add New Item"}</h2>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-[#F4F3EF]"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-8 space-y-5">
              <div>
                <label className="field-label">Name *</label>
                <input type="text" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="item-input-name"
                  className="field" placeholder="Item name" />
              </div>
              <div>
                <label className="field-label">Category</label>
                <select value={form.category_id} onChange={(e) => setForm({...form, category_id: e.target.value})} data-testid="item-input-category"
                  className="field">
                  <option value="">Select category</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="field-label">Description</label>
                <textarea rows={3} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} data-testid="item-input-desc"
                  className="field resize-y" placeholder="Item description" />
              </div>
              <div>
                <label className="field-label">Images (URLs)</label>
                {form.images.map((img, idx) => (
                  <div key={idx} className="flex gap-2 mb-2">
                    <input type="url" value={img} onChange={(e) => updateImage(idx, e.target.value)} data-testid={`item-input-image-${idx}`}
                      className="field !w-auto flex-1 !py-2" placeholder="https://..." />
                    {form.images.length > 1 && <button type="button" onClick={() => removeImage(idx)} className="text-red-500 hover:bg-red-50 p-1"><X size={16} /></button>}
                  </div>
                ))}
                <button type="button" onClick={addImageField} className="text-xs font-bold text-[#00739E] hover:underline">+ Add Another Image</button>
              </div>
              <div>
                <label className="field-label">Starting Price (&#8377;)</label>
                <input type="number" min="0" step="0.01" value={form.starting_price} onChange={(e) => setForm({...form, starting_price: parseFloat(e.target.value) || 0})} data-testid="item-input-price"
                  className="field" />
              </div>
              <div>
                <label className="field-label">Available Colors</label>
                {colors.length === 0 ? (
                  <p className="text-xs text-[#5A5955]">No colors in master data. Add colors first.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {colors.map(c => {
                      const selected = form.available_colors.includes(c.id);
                      return (
                        <button type="button" key={c.id} onClick={() => toggleColor(c.id)} data-testid={`color-toggle-${c.id}`}
                          className={`flex items-center gap-2 px-3 py-1.5 border text-xs font-medium transition-colors ${selected ? "border-[#161616] bg-[#161616] text-white" : "border-[#D9D7D0] text-[#5A5955] hover:border-[#161616]"}`}>
                          <div className="w-4 h-4 border border-white/30" style={{ backgroundColor: c.color_code }} />
                          {c.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {formError && <p className="text-sm text-red-600 bg-red-50 px-4 py-2 border border-red-200">{formError}</p>}
              <button type="submit" disabled={submitting} data-testid="submit-item-btn"
                className="w-full bg-[#161616] text-white font-bold px-8 py-4 text-sm hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_#E5007D] transition-all duration-200 disabled:opacity-50">
                {submitting ? "Saving..." : editing ? "Update Item" : "Add Item"}
              </button>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
